import { Info } from "phosphor-react";
import { ReactNode, useCallback, useState } from "react";
import Alert from "../../components/alert";
import { InputField } from "../../components/input-functions";
import ToolTip, { Side } from "../../components/tooltip";
import useScreenName from "../../hooks/useScreenName";
import "./dashboardStyles.css";
import MultiMonthLoanStats from "./multi-month-loan-stats";
import OneMonthLoanStats from "./one-month-loan-stats";
import OverallMonthlyFinancialStats from "./overall-monthly-financials-stats";
import Summary from "./summary";
import {
  DashboardSectionAuthStatus,
  DashboardSectionKey,
} from "./types";

const DASHBOARD_SECTIONS: DashboardSectionKey[] = [
  "summary",
  "overall",
  "oneMonth",
  "multiMonth",
];

function getMonthTypeInputValueFromDate(date: Date | null): string {
  if (!date) return "";
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const monthWithPaddingAtStart = month.toString().padStart(2, "0");
  return `${year}-${monthWithPaddingAtStart}`;
}

export default function DashboardScreen() {
  useScreenName("Dashboard");

  const [selectedMonthAndYear, updateSelectedMonthAndYear] =
    useState<Date | null>(new Date());

  const [sectionAuthStatuses, setSectionAuthStatuses] = useState<
    Partial<Record<DashboardSectionKey, DashboardSectionAuthStatus>>
  >({});

  const reportSectionAuthStatus = useCallback(
    (section: DashboardSectionKey, status: DashboardSectionAuthStatus) => {
      setSectionAuthStatuses((prev) => ({ ...prev, [section]: status }));
    },
    [],
  );

  const allSectionsForbidden =
    DASHBOARD_SECTIONS.length === Object.keys(sectionAuthStatuses).length &&
    DASHBOARD_SECTIONS.every(
      (section) => sectionAuthStatuses[section] === "forbidden",
    );

  if (allSectionsForbidden) {
    return (
      <div className="dashboard-container">
        <Alert type="danger" message="Not authorized to view dashboard." />
      </div>
    );
  }

  return (
    <>
      <Summary onAuthorizationChange={reportSectionAuthStatus} />
      <div className="d-flex flex-row-reverse">
        <InputField
          type="month"
          id="fromDate"
          className="form-control input-login"
          placeholder="From Date"
          value={getMonthTypeInputValueFromDate(selectedMonthAndYear)}
          onChange={(e) =>
            updateSelectedMonthAndYear(e.currentTarget.valueAsDate)
          }
          required
        />
      </div>
      <OverallMonthlyFinancialStats
        selectedDate={selectedMonthAndYear}
        onAuthorizationChange={reportSectionAuthStatus}
      />
      <OneMonthLoanStats
        selectedDate={selectedMonthAndYear}
        onAuthorizationChange={reportSectionAuthStatus}
      />
      <MultiMonthLoanStats
        selectedDate={selectedMonthAndYear}
        onAuthorizationChange={reportSectionAuthStatus}
      />
    </>
  );
}

interface RenderToolTipProps {
  content: ReactNode;
  contentPosition: Side;
}

function RenderToolTip({ content, contentPosition }: RenderToolTipProps) {
  return (
    <ToolTip>
      <ToolTip.Trigger className="mt-2">
        <Info size={20} color="var(--secondary)" weight="fill" />
      </ToolTip.Trigger>
      <ToolTip.Content side={contentPosition}>
        {content}
        <ToolTip.Arrow side={contentPosition} />
      </ToolTip.Content>
    </ToolTip>
  );
}
